import React, { useRef, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import BulletListItem from "@tiptap/extension-bullet-list";
import Youtube from "@tiptap/extension-youtube";
import DOMPurify from "dompurify";

import ImageExtension from "./CustomExtensions/ImageExtension";
import AttributesPreserver from "./CustomExtensions/AttributesPreserver";
import ParagraphExtension from "./CustomExtensions/ParagraphExtension";

import EditorMenu from "./EditorMenu/EditorMenu";
import UploadDoc from "./UploadDoc";
import EditorFooter from "./EditorFooter";
//import FloatingVariables from "./FloatingMenus/FloatingVariables";

const theme = {
	rainbow: {
		palette: {
			brand: "#002E6E",
		}
	}
};

DOMPurify.addHook("afterSanitizeAttributes", function (node) {
	// set all elements owning target to target=_blank
	if ("target" in node) {
		node.setAttribute("target", "_blank");
		node.setAttribute("rel", "noopener");
	}
});

const RTE = ({
	onChange,
	value,
	minimalistic = false,
	className,
	menuClassName,
	editorClassName,
	style = {},
	variables = false,
	customVariablesList = [],
	placeholder = "Write something...",
	purify = true,
	uploadDocPrompt = true,
	disabled = false,
	minHeight = 200,
	showHighlighters = true,
	showTextAlignment = true,
	showLists = true,
	showLinks = true,
	showHistory = true,
	...props
}) => {
	const editorWrapperRef = useRef(null);
	const [initialValueLoaded, setInitialValueLoaded] = useState(false);

	/** DO NOT REMOVE THIS. THIS IS WORK IN PROGRESS */
	//const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
	//const [showFloatingVariables, setShowFloatingVariables] = useState("none");

	/*const getCursorPosition = () => {
							const selection = editor.state.selection;
							const { from } = selection;
							const coords = editor.view.coordsAtPos(from);

							// Get the bounding rectangle of the editor wrapper
							const editorWrapperRect = editorWrapperRef.current.getBoundingClientRect();

							// Calculate cursor position relative to the editor wrapper
							return {
								top: coords.top - editorWrapperRect.top,
								left: coords.left - editorWrapperRect.left
							};
						};*/

	const TiptapBugPatch = html => {
		const savedHtmlString = html;
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = savedHtmlString;

		const trailingBreakElements = tempDiv.querySelectorAll(".ProseMirror-trailingBreak");

		trailingBreakElements.forEach(element => {
			const previousElement = element.previousElementSibling;
			if (previousElement && previousElement.tagName === "BR") {
				previousElement.remove();
			}
			element.remove();
		});

		const processedHtmlString = tempDiv.innerHTML;
		return processedHtmlString;
	};

	const handleKeyDown = event => {
		if (event.key === "Enter") {
			/*const currentPosition = editor.state.selection.from;
								const html = editor
									.getHTML()
									.replaceAll("<p></p>", "<br/>")
									.replaceAll("<p><br></p>", "<br>");
								console.log("on key down - ", html);
								editor.commands.setContent(html);
								editor.chain().setTextSelection(currentPosition).run();
								event.preventDefault();*/
			return;
		}
		/*if (event.key === "Backspace") {
								const currentPosition = editor.state.selection.from;
								const html = editor
									.getHTML()
									.replaceAll("<p></p>", "<br/>")
									.replaceAll("<p><br></p>", "<br>");
								console.log("on key down bkspc - ", html);
								editor.commands.setContent(html);
								editor.chain().setTextSelection(currentPosition).run();
								event.preventDefault();
								return;
							}*/
		/*if (variables && event) {
								if (event.key === "{" && showFloatingVariables === "none") {
									const position = getCursorPosition();
									setCursorPosition(position);
									setShowFloatingVariables("block");
									event.preventDefault();
									return;
								}
								if (event.key === "{" && showFloatingVariables === "block") {
									setShowFloatingVariables("none");
									return;
								}
								if (event.key === "}" && showFloatingVariables === "block") {
									setShowFloatingVariables("none");
									return;
								}
								if (event.key === " " && showFloatingVariables === "block") {
									setShowFloatingVariables("none");
									editor.chain().focus().insertContent("{").run();
									return;
								}
								if (event.key === "Escape" && showFloatingVariables === "block") {
									setShowFloatingVariables("none");
									return;
								}
							}*/
	};

	const extensions = [
		StarterKit.configure({
			orderedList: {
				keepMarks: true,
				keepAttributes: false
			}
		}),
		ParagraphExtension,
		//LineBreakExtension,
		//AttributesPreserver,
		Underline,
		BulletListItem.configure({
			keepMarks: true,
			keepAttributes: true
		}),
		TextStyle,
		Color,
		Highlight.configure({ multicolor: true }),
		TextAlign,
		Placeholder.configure({ placeholder }),
		Link.configure({ protocols: ["ftp", "mailto"], inline: true }),
		Table.configure({
			resizable: true,
			HTMLAttributes: { class: "custom-table" }
		}),
		TableRow,
		TableHeader,
		TableCell.configure({
			resizable: true
		}),
		ImageExtension.configure({
			inline: true
		}),
		Youtube
	];

	const editor = useEditor({
		content: value,
		extensions,
		editable: !disabled,
		injectCSS: false,
		//injectCSS: false,
		onUpdate: () => {
			const html = TiptapBugPatch(editor.getHTML());
			if (onChange) onChange(purify ? DOMPurify.sanitize(html) : html);
		},
		onDestroy: () => {
			return false;
		},
		editorProps: {
			handleDOMEvents: {
				drop: (view, e) => {
					e.preventDefault();
				},
				// This code was copied form chatgpt
				paste: (view, event) => {
					const clipboardData = event.clipboardData;

					if (clipboardData) {
						const items = clipboardData.items;
						const types = clipboardData.types;
						let hasImage = false;

						// Check if clipboard contains image files
						if (types.includes("Files")) {
							for (const item of items) {
								if (item.kind === "file" && item.type.startsWith("image/")) {
									hasImage = true;
									break;
								}
							}
						}

						if (hasImage) {
							// Prevent pasting
							event.preventDefault();

							// Manually handle pasting of other content
							const text = clipboardData.getData("text/plain");
							if (text) {
								const tr = view.state.tr.insertText(text);
								view.dispatch(tr);
							}

							return true;
						}
					}

					return false; // Let other content be pasted normally
				}
			},
			transformPastedHTML(html) {
				return html.replace(/<img.*?>/g, "");
			}
		},
		dropCursor: { width: 0, color: "transparent" },
		parseOptions: { preserveWhitespace: false }
	});

	useEffect(() => {
		if (editor) {
			editor.view.dom.addEventListener("keydown", handleKeyDown);
		}
		return () => {
			if (editor) {
				editor.view.dom.removeEventListener("keydown", handleKeyDown);
			}
		};
	}, [editor]);

	useEffect(() => {
		if (editor && !initialValueLoaded) {
			const currentPosition = editor.state.selection.from;
			editor.commands.setContent(value);
			editor.chain().setTextSelection(currentPosition).run();
			setInitialValueLoaded(true);
		}
	}, [value]);

	if (!editor) {
		return null;
	}

	return (
		<>
			<div
				className={`w-100 editor-wrapper${className ? ` ${className}` : ""}`}
				ref={editorWrapperRef}
				style={{ ...style }}
			>
				<EditorMenu
					minimalistic={minimalistic}
					className={menuClassName}
					editor={editor}
					variables={variables}
					customVariablesList={customVariablesList}
					showHighlighters={showHighlighters}
					disabled={disabled}
					showTextAlignment={showTextAlignment}
					showLists={showLists}
					showLinks={showLinks}
					showHistory={showHistory}
				/>
				{disabled ? (
					<div
						className="editor-container"
						dangerouslySetInnerHTML={{
							__html: purify ? DOMPurify.sanitize(value) : value
						}}
					></div>
				) : (
						<EditorContent
							editor={editor}
							className={`editor-container${
								editorClassName ? ` ${editorClassName}` : ""
								}`}
							{...props}
						/>
					)}
				<EditorFooter editor={editor} />
				{uploadDocPrompt && <UploadDoc editor={editor} onChange={onChange} theme={theme} />}
			</div>
			{/** DO NOT REMOVE THIS. THIS IS WORK IN PROGRESS */}
			{/*variables && (
									<div
										style={{
											display: showFloatingVariables,
											position: "absolute",
											left: cursorPosition.left,
											top: cursorPosition.top,
											zIndex: 1
										}}
									>
										<FloatingVariables editor={editor} customVariablesList={customVariablesList} />
									</div>
								)*/}
			<style>{`
									.editor-wrapper {
										border: 2px solid #eee;
										border-radius: 10px;
										display: flex;
										flex-direction: column;
										font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif;
									}

									.editor-container {
										margin: 5px 20px;
										min-height: ${minHeight}px;
										overflow: auto;
									}

									.editor-container .tiptap {
										outline: none;
									}

									.editor-container .tiptap p.is-editor-empty:first-child::before {
										color: #adb5bd;
										content: attr(data-placeholder);
										float: left;
										height: 0;
										pointer-events: none;
									}

									.editor-container .tiptap li > p {
										margin: 0 !important;
										display: inline-block !important;
									}

									.editor-container .tiptap a > p {
										display: inline !important;
									}

									.editor-container .tiptap ul, .editor-container .tiptap ol {
									list-style-position: inside !important;
									}

									.editor-container .tiptap table {
										border-collapse: collapse;
										margin: 0;
										overflow: hidden;
										table-layout: fixed;
										width: 100%;
									}

									.editor-container .tiptap td, .editor-container .tiptap th {
										border: 2px solid #ced4da;
										box-sizing: border-box;
										min-width: 1em;
										padding: 3px 5px;
										position: relative;
										vertical-align: top;
									}

									.editor-container .tiptap td > *, .editor-container .tiptap th > * {
										margin-bottom: 0;
									}

									.editor-container .tiptap th {
										background-color: #f1f3f5;
										font-weight: bold;
										text-align: left;
									}

									.editor-container .tiptap table .selectedCell:after {
										background: rgba(200, 200, 255, 0.4);
										content: "";
										left: 0;
										right: 0;
										top: 0;
										bottom: 0;
										pointer-events: none;
										position: absolute;
										z-index: 2;
									}

									.editor-container .tiptap table .column-resize-handle {
										background-color: #adf;
										bottom: -2px;
										position: absolute;
										right: -2px;
										pointer-events: none;
										top: 0;
										width: 4px;
									}

									.editor-container .tiptap p {
										margin: 0;
									}

									.editor-container .tiptap .tableWrapper {
										padding: 1rem 0;
										overflow-x: auto;
									}

									.editor-container .tiptap .resize-cursor {
										cursor: ew-resize;
										cursor: col-resize;
									}

									.upload-doc-prompt {
										background: #f5f5f5;
										padding: 5px;
									}

									.is-editor-empty {
										min-height: 200px;
									}

									.editor-container .image-resize-container {
										max-width: 100%; 
										height: auto !important;
										display: inline-block; 
										resize: both; 
										overflow: auto;
									}

									.editor-container .image-reference {
										padding: 5px; 
										border: 1px solid #2F2F2F; 
									}

									.editor-container .ProseMirror-trailingBreak {
										/*display: none;*/
									}
								`}</style>
		</>
	);
};

export default RTE;
